import { useEffect, useState, type FormEvent } from 'react'
import { MinimalInput, MinimalSegmentedControl } from '@ovasabi/ui-minimal'
import { commandPayload, commands } from '../../lib/runtime'
import { displayTitle } from '../../lib/displayName'
import { formatPhone } from '../../lib/phone'
import * as S from './OpsStyle'
import { formatWhen } from './opsData'

/**
 * Everyone with an account: customers, chefs and riders.
 *
 * Read from the server rather than the profile projection, because the
 * projection is the public name-and-face directory and deliberately carries no
 * email, phone or account status. Support can look people up; only operations
 * can suspend or restore an account, and the server enforces both.
 */

export interface OpsUser {
  profile_id: string
  display_name: string
  email: string
  phone: string
  city: string
  status: string
  roles: string[]
  order_count: number
  created_at: string
  suspension_reason: string
}

type RoleFilter = '' | 'customer' | 'chef' | 'rider'

const ROLE_LABEL: Record<string, string> = {
  customer: 'Customer',
  chef: 'Chef',
  market_vendor: 'Store',
  rider: 'Rider',
  support: 'Support',
  ops: 'Operations',
  admin: 'Admin',
}

const asUsers = (value: unknown): OpsUser[] =>
  Array.isArray(value) ? value.filter((row): row is OpsUser => Boolean(row) && typeof row === 'object') : []

export const OpsUsersPage = ({ canManage }: { canManage: boolean }) => {
  const [draft, setDraft] = useState('')
  const [query, setQuery] = useState('')
  const [role, setRole] = useState<RoleFilter>('')
  const [cursors, setCursors] = useState<string[]>([''])
  const [users, setUsers] = useState<OpsUser[]>([])
  const [next, setNext] = useState('')
  const [busy, setBusy] = useState(true)
  const [error, setError] = useState('')
  const [acting, setActing] = useState<OpsUser | null>(null)
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const cursor = cursors[cursors.length - 1] ?? ''

  useEffect(() => {
    let active = true
    setBusy(true)
    setError('')
    void commands.account
      .opsListUsers({ query, role, cursor })
      .then((response) => {
        if (!active) return
        const payload = commandPayload(response)
        if (!Array.isArray(payload.users)) throw new Error('The server returned an incomplete list.')
        setUsers(asUsers(payload.users))
        setNext(String(payload.next_cursor ?? ''))
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err.message : 'Could not load accounts.')
      })
      .finally(() => {
        if (active) setBusy(false)
      })
    return () => {
      active = false
    }
  }, [query, role, cursor])

  const search = (event: FormEvent) => {
    event.preventDefault()
    setCursors([''])
    setQuery(draft.trim())
  }

  const changeStatus = async (user: OpsUser, status: 'active' | 'suspended') => {
    if (status === 'suspended' && !reason.trim()) {
      setError('Say why this account is being suspended.')
      return
    }
    setSaving(true)
    setError('')
    try {
      const updated = (commandPayload(await commands.account.setUserStatus(user.profile_id, status, reason.trim())).user ?? {}) as OpsUser
      if (!updated.profile_id) throw new Error('The change was not confirmed.')
      setUsers((rows) => rows.map((row) => (row.profile_id === updated.profile_id ? { ...row, ...updated } : row)))
      setActing(null)
      setReason('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update the account.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div>
        <S.H1>People</S.H1>
        <S.Lede>Find anyone with an account by name, email or phone number.</S.Lede>
      </div>

      <S.Card>
        <form onSubmit={search} style={{ display: 'grid', gap: 12 }}>
          <MinimalInput
            label="Search"
            placeholder="Name, email or phone number"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          <MinimalSegmentedControl<RoleFilter>
            ariaLabel="Account type"
            value={role}
            onChange={(value) => {
              setCursors([''])
              setRole(value)
            }}
            variant="brand"
            options={[
              { value: '', label: 'Everyone' },
              { value: 'customer', label: 'Customers' },
              { value: 'chef', label: 'Chefs' },
              { value: 'rider', label: 'Riders' },
            ]}
          />
        </form>
      </S.Card>

      {error && <S.ErrorNote role="alert">{error}</S.ErrorNote>}

      {acting && (
        <S.Card>
          <S.CardTitle>
            {acting.status === 'suspended' ? 'Restore' : 'Suspend'} {displayTitle(acting.display_name)}
          </S.CardTitle>
          <S.Note>
            {acting.status === 'suspended'
              ? 'They can sign in again straight away.'
              : 'They can’t sign in again until you restore the account. A session they already have open keeps working until it expires (up to a day). Their orders and history stay. To stop a kitchen selling, suspend it from Kitchens instead.'}
          </S.Note>
          {acting.status !== 'suspended' && (
            <MinimalInput
              label="Reason (required)"
              placeholder="What happened? The next operator will see this."
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            {acting.status === 'suspended' ? (
              <S.Action disabled={saving} onClick={() => changeStatus(acting, 'active')}>
                {saving ? 'Restoring…' : 'Restore account'}
              </S.Action>
            ) : (
              <S.Danger disabled={saving || !reason.trim()} onClick={() => changeStatus(acting, 'suspended')}>
                {saving ? 'Suspending…' : 'Suspend account'}
              </S.Danger>
            )}
            <S.Action disabled={saving} onClick={() => { setActing(null); setReason('') }}>
              Cancel
            </S.Action>
          </div>
        </S.Card>
      )}

      <S.Card>
        <S.CardTitle>{query ? `Results for “${query}”` : 'Newest first'}</S.CardTitle>
        {busy ? (
          <S.Empty>Loading…</S.Empty>
        ) : users.length === 0 ? (
          <S.Empty>{query ? 'Nobody matches that search.' : 'No accounts yet.'}</S.Empty>
        ) : (
          <S.TableScroll>
            <S.Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Type</th>
                  <th>Orders</th>
                  <th>Status</th>
                  <th>Joined</th>
                  {canManage && <th />}
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.profile_id}>
                    <td>
                      {displayTitle(user.display_name)}
                      {user.city && <S.Note style={{ margin: 0 }}>{user.city}</S.Note>}
                    </td>
                    <td>
                      {user.email && <div>{user.email}</div>}
                      {user.phone && <div>{formatPhone(user.phone)}</div>}
                      {!user.email && !user.phone && <S.Pill $tone="flat">none</S.Pill>}
                    </td>
                    <td>{user.roles.map((r) => ROLE_LABEL[r] ?? r).join(', ') || '—'}</td>
                    <td>{user.order_count}</td>
                    <td>
                      {/* A new profile starts as 'pending', which gates nothing; to an
                          operator anything not suspended is a working account. */}
                      <S.Pill
                        $tone={user.status === 'suspended' ? 'bad' : 'ok'}
                        title={user.suspension_reason || undefined}
                      >
                        {user.status === 'suspended' ? 'suspended' : 'active'}
                      </S.Pill>
                    </td>
                    <td>{formatWhen(user.created_at)}</td>
                    {canManage && (
                      <td>
                        {user.roles.some((r) => r === 'ops' || r === 'admin' || r === 'support') ? null : user.status === 'suspended' ? (
                          <S.Action onClick={() => { setActing(user); setReason('') }}>Restore</S.Action>
                        ) : (
                          <S.Danger onClick={() => { setActing(user); setReason('') }}>Suspend</S.Danger>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </S.Table>
          </S.TableScroll>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <S.Action disabled={cursors.length < 2 || busy} onClick={() => setCursors((c) => c.slice(0, -1))}>
            Previous
          </S.Action>
          <S.Action disabled={!next || busy} onClick={() => setCursors((c) => [...c, next])}>
            Next
          </S.Action>
        </div>
      </S.Card>
    </>
  )
}
